import { useState, useMemo } from 'react';
import { Table, TableColumn, Pagination } from '@components/common/table';
import { Button } from '@components/common/button';
import { Input } from '@components/common/input';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  DollarSign,
  CreditCard,
  Smartphone,
  Banknote,
  Building,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Receipt,
  HelpCircle
} from 'lucide-react';
import { Payment, PaymentMethod, PaymentStatus } from '@/types/payment_types';

interface PaymentListProps {
  payments: Payment[];
  is_loading?: boolean;
  on_add_payment: () => void;
  on_view_receipt: (payment: Payment) => void;
  on_export?: () => void;
}

export const PaymentList: React.FC<PaymentListProps> = ({
  payments,
  is_loading = false,
  on_add_payment,
  on_view_receipt,
  on_export
}) => {
  const [search_term, set_search_term] = useState('');
  const [filter_method, set_filter_method] = useState<PaymentMethod | 'all'>('all');
  const [filter_status, set_filter_status] = useState<PaymentStatus | 'all'>('all');
  const [current_page, set_current_page] = useState(1);
  const [sort_config, set_sort_config] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'payment_date', direction: 'desc' });

  const items_per_page = 10;

  // Filter and search payments
  const filtered_payments = useMemo(() => {
    let result = [...payments];

    // Apply method filter
    if (filter_method !== 'all') {
      result = result.filter(payment => payment.payment_method === filter_method);
    }

    // Apply status filter
    if (filter_status !== 'all') {
      result = result.filter(payment => payment.status === filter_status);
    }

    // Apply search
    if (search_term) {
      const term = search_term.toLowerCase();
      result = result.filter(payment =>
        payment.payment_number.toLowerCase().includes(term) ||
        payment.client_name.toLowerCase().includes(term) ||
        payment.invoice_number?.toLowerCase().includes(term) ||
        payment.reference_number?.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      const a_value = a[sort_config.key as keyof Payment];
      const b_value = b[sort_config.key as keyof Payment];

      if (a_value === undefined || b_value === undefined) return 0;

      if (a_value < b_value) return sort_config.direction === 'asc' ? -1 : 1;
      if (a_value > b_value) return sort_config.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [payments, filter_method, filter_status, search_term, sort_config]);

  // Pagination
  const total_pages = Math.ceil(filtered_payments.length / items_per_page);
  const paginated_payments = filtered_payments.slice(
    (current_page - 1) * items_per_page,
    current_page * items_per_page
  );

  const handle_sort = (key: string) => {
    set_sort_config(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const get_method_badge = (method: PaymentMethod) => {
    const styles: Record<PaymentMethod, string> = {
      cash: 'bg-green-100 text-green-700',
      bank_transfer: 'bg-blue-100 text-blue-700',
      mpesa: 'bg-purple-100 text-purple-700',
      cheque: 'bg-orange-100 text-orange-700',
      card: 'bg-pink-100 text-pink-700',
      other: 'bg-gray-100 text-gray-700'
    };

    const icons: Record<PaymentMethod, React.ReactNode> = {
      cash: <Banknote size={14} />,
      bank_transfer: <Building size={14} />,
      mpesa: <Smartphone size={14} />,
      cheque: <Receipt size={14} />,
      card: <CreditCard size={14} />,
      other: <HelpCircle size={14} />
    };

    const labels: Record<PaymentMethod, string> = {
      cash: 'Cash',
      bank_transfer: 'Bank Transfer',
      mpesa: 'M-Pesa',
      cheque: 'Cheque',
      card: 'Card',
      other: 'Other'
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[method]}`}>
        {icons[method]}
        {labels[method]}
      </span>
    );
  };

  const get_status_badge = (status: PaymentStatus) => {
    const styles: Record<PaymentStatus, string> = {
      completed: 'bg-success bg-opacity-10 text-success',
      pending: 'bg-warning bg-opacity-10 text-warning',
      failed: 'bg-error bg-opacity-10 text-error',
      refunded: 'bg-gray-100 text-gray-600',
      cancelled: 'bg-gray-100 text-gray-600'
    };

    const icons: Record<PaymentStatus, React.ReactNode> = {
      completed: <CheckCircle size={14} />,
      pending: <Clock size={14} />,
      failed: <XCircle size={14} />,
      refunded: <RefreshCw size={14} />,
      cancelled: <XCircle size={14} />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const format_currency = (amount: number) => {
    return `KES ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const columns: TableColumn<Payment>[] = [
    {
      key: 'payment_number',
      header: 'Payment #',
      sortable: true,
      render: (payment) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-forest-green bg-opacity-10 text-forest-green flex items-center justify-center">
            <DollarSign size={20} />
          </div>
          <div>
            <p className="font-semibold text-charcoal">{payment.payment_number}</p>
            {payment.invoice_number && (
              <p className="text-xs text-gray-500">
                Inv: {payment.invoice_number}
              </p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'client_name',
      header: 'Client',
      sortable: true,
      render: (payment) => (
        <div>
          <p className="font-medium text-charcoal">{payment.client_name}</p>
          {payment.reference_number && (
            <p className="text-xs text-gray-500">
              Ref: {payment.reference_number}
            </p>
          )}
        </div>
      )
    },
    {
      key: 'payment_date',
      header: 'Payment Date',
      sortable: true,
      render: (payment) => (
        <div className="text-sm">
          <p className="text-charcoal">
            {new Date(payment.payment_date).toLocaleDateString()}
          </p>
          <p className="text-xs text-gray-500">
            {new Date(payment.payment_date).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
      )
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      align: 'right',
      render: (payment) => (
        <div className="text-right">
          <p className="font-semibold text-forest-green">
            {format_currency(payment.amount)}
          </p>
        </div>
      )
    },
    {
      key: 'payment_method',
      header: 'Method',
      sortable: true,
      render: (payment) => get_method_badge(payment.payment_method)
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (payment) => get_status_badge(payment.status)
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (payment) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              on_view_receipt(payment);
            }}
            icon_left={<Eye size={14} />}
          >
            Receipt
          </Button>
        </div>
      )
    }
  ];

  // Calculate stats
  const stats = {
    total: filtered_payments.length,
    completed: filtered_payments.filter(p => p.status === 'completed').length,
    pending: filtered_payments.filter(p => p.status === 'pending').length,
    total_amount: filtered_payments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0),
    cash: filtered_payments
      .filter(p => p.payment_method === 'cash' && p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0),
    mpesa: filtered_payments
      .filter(p => p.payment_method === 'mpesa' && p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0),
    bank: filtered_payments
      .filter(p => p.payment_method === 'bank_transfer' && p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0)
  };

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 w-full sm:max-w-md">
          <Input
            type="text"
            placeholder="Search by payment #, client, invoice, reference..."
            value={search_term}
            onChange={(e) => set_search_term(e.target.value)}
            icon_left={<Search size={18} />}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Filter by Method */}
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-300">
            <Filter size={16} className="text-gray-500" />
            <select
              value={filter_method}
              onChange={(e) => set_filter_method(e.target.value as PaymentMethod | 'all')}
              className="text-sm border-none focus:outline-none cursor-pointer"
            >
              <option value="all">All Methods</option>
              <option value="cash">Cash</option>
              <option value="mpesa">M-Pesa</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="card">Card</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Filter by Status */}
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-300">
            <Filter size={16} className="text-gray-500" />
            <select
              value={filter_status}
              onChange={(e) => set_filter_status(e.target.value as PaymentStatus | 'all')}
              className="text-sm border-none focus:outline-none cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {on_export && (
            <Button
              variant="outline"
              size="md"
              icon_left={<Download size={18} />}
              onClick={on_export}
            >
              Export
            </Button>
          )}

          <Button
            variant="primary"
            size="md"
            icon_left={<Plus size={18} />}
            onClick={on_add_payment}
          >
            Record Payment
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Total Payments</p>
          <p className="text-2xl font-bold text-charcoal">{stats.total}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Completed</p>
          <p className="text-2xl font-bold text-success">{stats.completed}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Pending</p>
          <p className="text-2xl font-bold text-warning">{stats.pending}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-1 text-gray-500 mb-1">
            <DollarSign size={14} />
            <p className="text-xs">Total Received</p>
          </div>
          <p className="text-lg font-bold text-forest-green">
            {format_currency(stats.total_amount)}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-1 text-gray-500 mb-1">
            <Banknote size={14} />
            <p className="text-xs">Cash</p>
          </div>
          <p className="text-lg font-bold text-green-600">
            {format_currency(stats.cash)}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-1 text-gray-500 mb-1">
            <Smartphone size={14} />
            <p className="text-xs">M-Pesa</p>
          </div>
          <p className="text-lg font-bold text-purple-600">
            {format_currency(stats.mpesa)}
          </p>
        </div>
      </div>

      {/* Table */}
      <Table
        data={paginated_payments}
        columns={columns}
        is_loading={is_loading}
        on_row_click={on_view_receipt}
        sort_config={sort_config}
        on_sort={handle_sort}
        empty_message="No payments found. Record your first payment to get started!"
        striped
        hoverable
      />

      {/* Pagination */}
      {total_pages > 1 && (
        <Pagination
          current_page={current_page}
          total_pages={total_pages}
          on_page_change={set_current_page}
          items_per_page={items_per_page}
          total_items={filtered_payments.length}
        />
      )}
    </div>
  );
};